import React from 'react';
import { FaChevronDown, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';
import '../Dashboard.css';

const OverdueCards = ({ 
  title, 
  data, 
  navigate, 
  isCollapsed, 
  onToggleCollapse,
  config 
}) => {
  const {
    emptyStateCollapsed,
    emptyStateExpanded,
    badgeClassName,
    badgeLabel,
    countFieldName,
    featuresFieldName,
    daysFieldName,
    additionalSectionClass = '',
    getProjectUrgency,
    getFeatureUrgency,
    formatDays,
    formatBadgeMessage,
    isEmpty
  } = config;

  // Handle loading state (data is null) or empty array
  const hasData = data && Array.isArray(data) && data.length > 0;
  
  // Use custom isEmpty function if provided, otherwise use default check
  // isEmptyState determines if we should show empty state in expanded view
  // For work plan status: isEmpty should only be true when ALL resources have work plans (no missing resources)
  const isEmptyState = isEmpty ? isEmpty() : !hasData;
  
  // For work plan status, we always want to show the count in collapsed state
  // even if there are no missing resources, so we don't return early if isEmpty function exists
  if (!hasData && !isEmpty) {
    // Only return early if we have no data AND no custom isEmpty function
    return (
      <div className={`manager-stats-section ${additionalSectionClass}`}>
        <div 
          className="manager-stats-header"
          onClick={onToggleCollapse}
        >
          <div className="section-header-content">
            <h2 className="text-lg font-semibold text-default-font">{title}</h2>
            {isCollapsed && (
              <div className="section-summary">
                <span className="stats-success">{emptyStateCollapsed}</span>
              </div>
            )}
          </div>
          <button className="collapse-toggle-btn" type="button">
            {isCollapsed ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
          </button>
        </div>
        {!isCollapsed && (
          <div className="manager-stats-content">
            <div className="no-overdue-message">
              <span className="success-icon">✓</span>
              <span>{emptyStateExpanded}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  const totalCount = hasData ? data.reduce((sum, project) => sum + (project[countFieldName] || 0), 0) : 0;

  return (
    <div className={`manager-stats-section ${additionalSectionClass}`}>
      <div 
        className="manager-stats-header"
        onClick={onToggleCollapse}
      >
        <div className="section-header-content">
          <h2 className="text-lg font-semibold text-default-font">{title}</h2>
          {isCollapsed && (
            <div className="section-summary">
              {isEmptyState && !hasData && !isEmpty ? (
                // Show empty state only if no data and no custom isEmpty function
                <span className="stats-success">{emptyStateCollapsed}</span>
              ) : (
                <>
                  <span className={badgeClassName}>
                    {!isEmptyState && hasData && <FaExclamationTriangle size={10} />}
                    {formatBadgeMessage 
                      ? formatBadgeMessage(totalCount, hasData ? data.length : 0, badgeLabel)
                      : hasData
                        ? `${totalCount} ${badgeLabel} across ${data.length} ${data.length === 1 ? 'project' : 'projects'}`
                        : emptyStateCollapsed
                    }
                  </span>
                  {hasData && !isEmptyState && (
                    <div className="section-projects-preview">
                      {data.slice(0, 6).map((project) => {
                        const displayName = project.abbreviation || project.name.substring(0, 3).toUpperCase();
                        return (
                          <span key={project.id} className="project-preview-badge" title={project.name}>
                            {displayName}
                          </span>
                        );
                      })}
                      {data.length > 6 && (
                        <span className="project-preview-more"> +{data.length - 6}</span>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <button className="collapse-toggle-btn" type="button">
          {isCollapsed ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
        </button>
      </div>
      {!isCollapsed && (
        <div className="manager-stats-content">
          {hasData ? (
            // Always show list if there's data (missing resources)
            <>
              <div className="stats-expanded-summary">
                <span className="stats-expanded-count">{data.length}</span>
                <span className="stats-expanded-label">
                  {config.itemLabel ? (data.length === 1 ? config.itemLabel : `${config.itemLabel}s`) : (data.length === 1 ? 'project' : 'projects')}
                </span>
              </div>
              <div className="overdue-projects-grid">
            {data.map(project => {
              const features = project[featuresFieldName] || [];
              const urgencyLevel = getProjectUrgency(features);
              
              return (
                    <div 
                      key={project.id} 
                      className={`overdue-project-card urgency-${urgencyLevel} ${navigate ? 'clickable' : ''}`}
                      onClick={navigate ? () => navigate(`/projects/${project.id}`) : undefined}
                      style={navigate ? { cursor: 'pointer' } : { cursor: 'default' }}
                    >
                  <div className="project-card-header">
                    <div className="project-title-group">
                      <h3 className="project-name-compact">{project.name}</h3>
                      {project.abbreviation && (
                        <span className="project-abbr-compact">{project.abbreviation}</span>
                      )}
                      {project.projectAbbreviation && (
                        <span className="project-abbr-compact">{project.projectAbbreviation}</span>
                      )}
                    </div>
                    <div className={`overdue-count-pill urgency-${urgencyLevel}`}>
                      {project[countFieldName]}
                    </div>
                  </div>
                  <div className="project-features-list">
                    {features.slice(0, 4).map((feature) => {
                      const featureUrgency = getFeatureUrgency(feature);
                      const daysValue = feature[daysFieldName] || 0;
                      return (
                        <div key={feature.id} className={`feature-item urgency-${featureUrgency}`}>
                          <div className="feature-name-wrapper">
                            <span className="feature-name">{feature.name}</span>
                            {feature.resource_names && (
                              <span className="feature-resources" title={feature.resource_names}>
                                ({feature.resource_names.split(', ').slice(0, 2).join(', ')}{feature.resource_names.split(', ').length > 2 ? '...' : ''})
                              </span>
                            )}
                          </div>
                          <span className={`feature-days urgency-${featureUrgency}`}>
                            {formatDays(daysValue)}
                          </span>
                        </div>
                      );
                    })}
                    {features.length > 4 && (
                      <div className="feature-item feature-more">
                        <span className="feature-name">+{features.length - 4} more</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
              </div>
            </>
          ) : isEmptyState ? (
            // Show empty state only if isEmptyState is true (all resources have work plans)
            <div className="no-overdue-message">
              <span className="success-icon">✓</span>
              <span>{emptyStateExpanded}</span>
            </div>
          ) : (
            // Loading or no data state
            <div className="no-overdue-message">
              <span>{emptyStateExpanded}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OverdueCards;

